import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import toast from '../components/common/toast';
import { putawayService, binService } from '../services/api';
import { IoMdArrowDropdown } from 'react-icons/io';
import { MdLocationOn, MdEdit } from 'react-icons/md';
import { Table, Badge, AssignBinPopUp, BulkAssignBinPopUp, ViewPutawayPopup } from '../components/common';
import SkeletonTable from '../components/SkeletonTable';

const getTransactionTypeLabel = (type) =>
  type === 'INWARD' ? 'Inbound' : 'Return';

// Existing putaway statuses: PENDING, ASSIGNED, APPROVED, REJECT. Do not introduce PARTIAL, COMPLETED.
// OLD: ASSIGNED was manually selectable in the status dropdown.
// const PUTAWAY_STATUS_OPTIONS = ['PENDING', 'ASSIGNED', 'APPROVED', 'REJECT'];
// NEW: ASSIGNED is system-driven only (set during bin assignment), so it is
// not offered as a manual target status in the dropdown.
const PUTAWAY_STATUS_OPTIONS = ['PENDING', 'APPROVED', 'REJECT'];

const getStatusVariant = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'REJECT':
      return 'danger';
    case 'ASSIGNED':
      return 'info';
    case 'PENDING':
    default:
      return 'warning';
  }
};

function PutawayStatusCell({ status, putawayId, row, onSelectNewStatus, getStatusVariant }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const [dropdownAnchorRect, setDropdownAnchorRect] = useState(null);
  const isProductWise = Boolean(row?.inwardTransactionId);
  const isCompleted = status === 'APPROVED';

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (dropdownContentRef.current && dropdownContentRef.current.contains(e.target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (!status) return '—';
  const otherStatuses =
    status === 'ASSIGNED'
      ? PUTAWAY_STATUS_OPTIONS.filter((s) => s !== status && s !== 'PENDING')
      : PUTAWAY_STATUS_OPTIONS.filter((s) => s !== status);
  const showDropdown = !isCompleted && otherStatuses.length > 0;

  return (
    <div
      className="relative inline-flex items-center gap-1 cursor-pointer"
      ref={wrapRef}
      onClick={(e) => {
        e.stopPropagation();
        if (!showDropdown) return;
        const willOpen = !dropdownOpen;
        if (willOpen && wrapRef.current) {
          const rect = wrapRef.current.getBoundingClientRect();
          setDropdownAnchorRect(rect);
        }
        setDropdownOpen(willOpen);
      }}
    >
      <Badge variant={getStatusVariant(status)} className="inline-flex items-center gap-1 pr-1">
        <span>{status}</span>
        {showDropdown && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const willOpen = !dropdownOpen;
            if (willOpen && wrapRef.current) {
              const rect = wrapRef.current.getBoundingClientRect();
              setDropdownAnchorRect(rect);
            }
            setDropdownOpen(willOpen);
          }}
          className="p-0.5 rounded hover:bg-black/10 focus:outline-none focus:ring-1 focus:ring-offset-0"
          aria-label="Change status"
        >
          <IoMdArrowDropdown className="w-4 h-4" />
        </button>
        )}
      </Badge>
      {dropdownOpen && showDropdown && dropdownAnchorRect && createPortal(
        <div
          ref={dropdownContentRef}
          className="mt-1 min-w-[160px] rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            position: 'fixed',
            left: dropdownAnchorRect.left,
            top: dropdownAnchorRect.bottom + 4,
            zIndex: 99999,
          }}
        >
          <ul className="py-1 list-none" role="listbox">
            {otherStatuses.map((s) => (
              <li key={s} role="option">
                <button
                  type="button"
                  onClick={() => { onSelectNewStatus(s); setDropdownOpen(false); }}
                  className="w-full block px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}

function getPutawaySelectionKey(row) {
  return String(row?.putawayId || row?._id || '').trim();
}

function isPutawayBulkEligible(row) {
  const isProductWise = Boolean(row?.inwardTransactionId);
  const hasUsableReceivedQty = Number(row?.receivedQuantity) > 0;
  const canUseAssignFlow = isProductWise || hasUsableReceivedQty;
  const status = String(row?.status || '').toUpperCase();
  if (!canUseAssignFlow) return false;
  if (status === 'APPROVED' || status === 'REJECT') return false;
  return status === 'PENDING' || status === 'ASSIGNED';
}

export function PutawayManagement() {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [putawayTasks, setPutawayTasks] = useState([]);
  const [bins, setBins] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [assignBinPopUpOpen, setAssignBinPopUpOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [assignBins, setAssignBins] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmPayload, setStatusConfirmPayload] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [viewDetailsError, setViewDetailsError] = useState(null);
  const [viewDetailsData, setViewDetailsData] = useState(null);
  const [viewDetailsId, setViewDetailsId] = useState(null);
  const [selectedPutawayIds, setSelectedPutawayIds] = useState([]);
  const [selectedPutawayById, setSelectedPutawayById] = useState({});
  const [allEligiblePutawayIds, setAllEligiblePutawayIds] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const selectAllLoadingRef = useRef(false);

  const {
    data: putawayResponse,
    isLoading,
    isFetching,
    refetch: refetchPutawayTasks,
  } = useQuery({
    queryKey: ['putawayTasks', filterStatus, page, limit],
    queryFn: async () => {
      const params = { page, limit };
      if (filterStatus) params.status = filterStatus;
      return putawayService.getPutawayTasks(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const total = Number(putawayResponse?.total) || 0;
  const totalPages = Math.max(
    1,
    Number(putawayResponse?.totalPages) || Math.ceil(total / limit) || 1
  );
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * limit, total);

  useEffect(() => {
    const list = Array.isArray(putawayResponse?.data) ? putawayResponse.data : [];
    setPutawayTasks(list);
    console.log('[PUTAWAY][VISIBLE_ROWS]', {
      rendered: list.length,
      page,
      limit,
      total,
      filterStatus: filterStatus || 'ALL',
    });
  }, [putawayResponse, page, limit, filterStatus, total]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  useEffect(() => {
    setSelectedPutawayIds([]);
    setSelectedPutawayById({});
    setAllEligiblePutawayIds([]);
  }, [filterStatus]);

  useEffect(() => {
    const onRefresh = () => refetchPutawayTasks();
    window.addEventListener('putaway-refresh', onRefresh);
    return () => window.removeEventListener('putaway-refresh', onRefresh);
  }, [refetchPutawayTasks]);

  useEffect(() => {
    fetchBins();
  }, []);

  const loading = isLoading || isFetching;

  const fetchBins = async () => {
    try {
      const result = await binService.getBins({});
      setBins(result.data || []);
    } catch (error) {
      console.error('Error fetching bins:', error);
    }
  };

  const handleAssignBin = async (row) => {
    setSelectedTask(row);
    setAssignBinPopUpOpen(true);
    // Bins are loaded inside popup by condition (GET /putaway/:id/bins-available?condition=...).
    setAssignBins([]);

    // Fetch enriched putaway detail (GET /putaway/:id) including existing assignments for edit.
    try {
      const response = await putawayService.getPutawayTaskById(row?._id);
      const detail = response?.data?.data ?? response?.data ?? {};
      setSelectedTask((prev) => ({ ...(prev || {}), ...(detail || {}) }));
    } catch (e) {
      console.error('Error fetching putaway detail:', e);
    }
  };

  const handleApplyBin = async (toBinIdOrAssignments) => {
    if (!selectedTask) {
      console.log('[PutawayManagement] handleApplyBin: validation failure, selectedTask is null, returning');
      return;
    }
    const isProductWise = Boolean(selectedTask?.inwardTransactionId);

    try {
      setAssignLoading(true);
      if (Array.isArray(toBinIdOrAssignments) || toBinIdOrAssignments?.assignments) {
        const body = Array.isArray(toBinIdOrAssignments)
          ? { assignments: toBinIdOrAssignments }
          : { condition: toBinIdOrAssignments.condition, assignments: toBinIdOrAssignments.assignments };
        console.log('[PutawayManagement] Calling assign-bins API', { putawayId: selectedTask._id, body });
        const response = await putawayService.assignPutawayBins(selectedTask._id, body);
        console.log('[PutawayManagement] assign-bins API success response', response);
      } else if (!isProductWise && typeof toBinIdOrAssignments === 'string') {
        console.log('[PutawayManagement] Calling completePutaway API', { putawayId: selectedTask._id, toBinId: toBinIdOrAssignments });
        const response = await putawayService.completePutaway(selectedTask._id, { toBinId: toBinIdOrAssignments });
        console.log('[PutawayManagement] completePutaway API success response', response);
      } else {
        console.log('[PutawayManagement] handleApplyBin: validation failure, unexpected payload (not product-wise assignments nor legacy toBinId), returning', { isProductWise, payload: toBinIdOrAssignments });
        return;
      }
      setAssignBinPopUpOpen(false);
      setSelectedTask(null);
      setAssignBins([]);
      await refetchPutawayTasks();
      fetchBins();
      toast.success('Putaway bin assignment saved successfully');
    } catch (error) {
      console.error('[PutawayManagement] assign bin catch: full error object', error);
      // const details = error?.details || {};
      // if (details.error && details.error.includes('capacity')) {
      //   toast.error(details.error, { autoClose: 5000 });
      // } else {
      toast.error(error?.message || 'Error assigning bin to putaway');
      // }
    } finally {
      setAssignLoading(false);
    }
  };


  const openStatusConfirm = (putawayId, currentStatus, newStatus) => {
    setStatusConfirmPayload({ id: putawayId, currentStatus, newStatus });
    setStatusConfirmOpen(true);
  };

  const fetchPutawayDetails = async (id) => {
    if (!id) return;
    setViewDetailsId(id);
    setViewDetailsLoading(true);
    setViewDetailsError(null);
    try {
      const res = await putawayService.getPutawayTaskById(id);
      setViewDetailsData(res?.data ?? res);
      setViewDetailsOpen(true);
    } catch (err) {
      setViewDetailsError(err?.message ?? 'Failed to load putaway details');
      setViewDetailsData(null);
      setViewDetailsOpen(true);
    } finally {
      setViewDetailsLoading(false);
    }
  };

  const handlePutawayRowClick = (row) => {
    if (row?._id) fetchPutawayDetails(row._id);
  };

  const handleStatusConfirmYes = async () => {
    if (!statusConfirmPayload) return;
    const { id, newStatus } = statusConfirmPayload;
    try {
      setStatusChangeLoading(true);
      await putawayService.updatePutawayStatus(id, { status: newStatus });
      setStatusConfirmOpen(false);
      setStatusConfirmPayload(null);
      await refetchPutawayTasks();
      toast.success('Status updated successfully');
    } catch (err) {
      console.error('Error updating putaway status:', err);
      toast.error(err?.message || 'Failed to update status');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const putawayColumns = [
    { key: 'putawayId', label: 'Putaway ID' },
    {
      key: 'transactionId',
      label: 'Transaction',
      render: (val, row) => row?.inwardTransactionId ? (row.inwardTransactionId?.transactionId ?? val) : val ?? '—',
    },
    {
      key: 'transactionType',
      label: 'Type',
      render: (val) => getTransactionTypeLabel(val),
    },
    {
      key: 'productId',
      label: 'Product',
      render: (val, row) => {
        const p = row?.product || val;
        return p ? `${p?.sku ?? ''} ${p?.description ? ` — ${p.description}` : ''}`.trim() || '—' : '—';
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (val, row) => (
        <PutawayStatusCell
          status={val}
          putawayId={row?._id}
          row={row}
          onSelectNewStatus={(newStatus) => openStatusConfirm(row?._id, val, newStatus)}
          getStatusVariant={getStatusVariant}
        />
      ),
    },
  ];

  const putawayRowActions = (row) => {
    const isProductWise = Boolean(row?.inwardTransactionId);
    const hasUsableReceivedQty = Number(row?.receivedQuantity) > 0;
    const canUseAssignFlow = isProductWise || hasUsableReceivedQty;
    const status = String(row?.status || '').toUpperCase();

    // Only product-wise putaway rows have Assign/Edit Bin behavior.
    // Fallback: if received quantity is available, allow assign flow even when inwardTransactionId is missing.
    if (!canUseAssignFlow) return [];
    // No bin actions for final states.
    if (status === 'APPROVED' || status === 'REJECT') return [];

    if (status === 'PENDING') {
      return [
        {
          label: 'Assign Bin',
          icon: MdLocationOn,
          onClick: (rowClicked) => handleAssignBin(rowClicked || row),
        },
      ];
    }

    if (status === 'ASSIGNED') {
      return [
        {
          label: 'Edit Putaway',
          icon: MdEdit,
          onClick: (rowClicked) => handleAssignBin(rowClicked || row),
        },
      ];
    }

    // Fallback: no actions for any other unexpected status.
    return [];
  };

  const selectedPutawayIdSet = useMemo(
    () => new Set((selectedPutawayIds || []).map((id) => String(id))),
    [selectedPutawayIds]
  );

  const bulkEligibleIndices = putawayTasks
    .map((row, index) => (isPutawayBulkEligible(row) ? index : -1))
    .filter((index) => index >= 0);

  const selectedRowIndices = useMemo(
    () =>
      putawayTasks
        .map((row, index) => {
          const key = getPutawaySelectionKey(row);
          return key && selectedPutawayIdSet.has(key) ? index : -1;
        })
        .filter((index) => index >= 0),
    [putawayTasks, selectedPutawayIdSet]
  );

  const selectedPutawaysForBulk = useMemo(() => {
    const rows = [];
    const seen = new Set();

    for (const id of selectedPutawayIds) {
      const fromPage = putawayTasks.find((row) => getPutawaySelectionKey(row) === id);
      const row = selectedPutawayById[id] || fromPage;
      if (row && isPutawayBulkEligible(row) && !seen.has(id)) {
        seen.add(id);
        rows.push(row);
      }
    }

    return rows;
  }, [selectedPutawayIds, selectedPutawayById, putawayTasks]);

  const fetchAllEligiblePutaways = useCallback(async () => {
    if (!total) {
      return { eligibleRows: [], eligibleIds: [] };
    }

    const params = { page: 1, limit: total };
    if (filterStatus) params.status = filterStatus;
    const response = await putawayService.getPutawayTasks(params);
    const allRows = Array.isArray(response?.data) ? response.data : [];
    const eligibleRows = allRows.filter(isPutawayBulkEligible);
    const eligibleIds = eligibleRows
      .map((row) => getPutawaySelectionKey(row))
      .filter(Boolean);

    return { eligibleRows, eligibleIds };
  }, [total, filterStatus]);

  const applyEligibleSelection = useCallback((eligibleRows, eligibleIds) => {
    setAllEligiblePutawayIds(eligibleIds);
    setSelectedPutawayIds(eligibleIds);
    const nextById = {};
    eligibleRows.forEach((row) => {
      const key = getPutawaySelectionKey(row);
      if (key) nextById[key] = row;
    });
    setSelectedPutawayById(nextById);
  }, []);

  const handleClearSelection = () => {
    setSelectedPutawayIds([]);
    setSelectedPutawayById({});
  };

  const handleBulkSelectRow = (newSelectedIndices) => {
    const nextIds = new Set(selectedPutawayIds);
    const nextById = { ...selectedPutawayById };

    putawayTasks.forEach((row, index) => {
      if (!isPutawayBulkEligible(row)) return;
      const key = getPutawaySelectionKey(row);
      if (!key) return;

      if (newSelectedIndices.includes(index)) {
        nextIds.add(key);
        nextById[key] = row;
      } else {
        nextIds.delete(key);
        delete nextById[key];
      }
    });

    setSelectedPutawayIds(Array.from(nextIds));
    setSelectedPutawayById(nextById);
  };

  const handleBulkSelectAll = async (indices) => {
    if (selectAllLoadingRef.current) return;

    if (!indices.length) {
      handleClearSelection();
      return;
    }

    selectAllLoadingRef.current = true;
    setSelectAllLoading(true);

    try {
      const { eligibleRows, eligibleIds } = await fetchAllEligiblePutaways();
      applyEligibleSelection(eligibleRows, eligibleIds);
    } catch (error) {
      console.error('Error selecting all putaways:', error);
      toast.error(error?.message || 'Failed to select all putaways');
    } finally {
      selectAllLoadingRef.current = false;
      setSelectAllLoading(false);
    }
  };

  const globalAllEligibleSelected =
    allEligiblePutawayIds.length > 0 &&
    allEligiblePutawayIds.every((id) => selectedPutawayIdSet.has(id));

  const allPageEligibleSelected =
    bulkEligibleIndices.length > 0 &&
    bulkEligibleIndices.every((index) => selectedRowIndices.includes(index));

  const selectAllChecked = allEligiblePutawayIds.length > 0
    ? globalAllEligibleSelected
    : allPageEligibleSelected;

  const selectAllIndeterminate = allEligiblePutawayIds.length > 0
    ? selectedPutawayIds.length > 0 && !globalAllEligibleSelected
    : selectedPutawayIds.length > 0 && !allPageEligibleSelected;

  const handleBulkAssignClick = () => {
    if (selectedPutawaysForBulk.length === 0) {
      toast.error('Please select putaway first');
      return;
    }
    setBulkAssignOpen(true);
  };

  const handleBulkAssignSuccess = async (res) => {
    const count = res?.data?.processedCount ?? selectedPutawaysForBulk.length;
    toast.success(`Bulk bin allocation completed for ${count} putaway${count === 1 ? '' : 's'}`);
    setSelectedPutawayIds([]);
    setSelectedPutawayById({});
    setAllEligiblePutawayIds([]);
    await refetchPutawayTasks();
    fetchBins();
  };

  return (
    <div className="page-container font-nunito">
     <div className="mb-6 w-full">
  <div
    className="flex items-center justify-between w-full flex-wrap gap-4"
    style={{
      backgroundColor: '#FFFFFF',
      border: '1px solid #EEF1F5',
      borderRadius: 28,
      padding: '16px 24px',
      boxShadow: '0px 0px 4px 0px #959DA526',
    }}
  >
    <h1
      style={{
        fontFamily: "Nunito",
        fontSize: 18,
        fontWeight: 600,
        color: "#161616",
        margin: 0,
      }}
    >
      Putaway Management
    </h1>

    <div className="relative flex items-center gap-3 flex-wrap justify-end">
      {selectAllLoading && (
        <span className="inline-flex items-center gap-2 text-sm text-gray-600">
          <span
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#188742]"
            aria-hidden="true"
          />
          Select All is processing…
        </span>
      )}
      {selectedPutawayIds.length > 0 && (
        <button
          type="button"
          onClick={handleClearSelection}
          disabled={selectAllLoading}
          className="h-11 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Clear Selection ({selectedPutawayIds.length})
        </button>
      )}
      <button
        type="button"
        onClick={handleBulkAssignClick}
        className={`h-11 rounded-full border px-5 text-sm font-semibold shadow-sm ${
          selectedPutawaysForBulk.length > 0
            ? 'border-[#188742] bg-[#188742] text-white hover:bg-[#15743a]'
            : 'border-gray-300 bg-gray-100 text-gray-400 hover:bg-gray-100'
        }`}
      >
        Bulk Assign Bin ({selectedPutawaysForBulk.length})
      </button>
      <select
        id="putaway-filter-status"
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold appearance-none cursor-pointer pr-10 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
        style={{ height: 44, paddingLeft: 18, paddingRight: 36, minWidth: 160 }}
      >
        <option value="">All statuses</option>
        <option value="PENDING">Pending</option>
        <option value="ASSIGNED">Assigned</option>
        <option value="APPROVED">Approved</option>
        <option value="REJECT">Rejected</option>
      </select>
      <span className="pointer-events-none absolute right-3 flex items-center text-[#188742]" style={{ top: '50%', transform: 'translateY(-50%)' }}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </span>
    </div>
  </div>
</div>


      <AssignBinPopUp
        open={assignBinPopUpOpen}
        onClose={() => {
          setAssignBinPopUpOpen(false);
          setSelectedTask(null);
          setAssignBins([]);
        }}
        onApply={handleApplyBin}
        putawayTask={selectedTask}
        bins={assignBins}
        loading={assignLoading}
      />
      <BulkAssignBinPopUp
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        selectedPutaways={selectedPutawaysForBulk}
        onSuccess={handleBulkAssignSuccess}
      />
      <ViewPutawayPopup
        open={viewDetailsOpen}
        onClose={() => {
          setViewDetailsOpen(false);
          setViewDetailsError(null);
          setViewDetailsData(null);
        }}
        loading={viewDetailsLoading}
        error={viewDetailsError}
        data={viewDetailsData}
        onRetry={viewDetailsId ? () => fetchPutawayDetails(viewDetailsId) : undefined}
      />
      {statusConfirmOpen && statusConfirmPayload && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99999 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="putaway-status-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end pr-4 pt-4">
              <button
                type="button"
                onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-2">
              <p id="putaway-status-confirm-title" className="text-base text-gray-800">
                Are you sure you want to change the status from <strong>{statusConfirmPayload.currentStatus}</strong> to <strong>{statusConfirmPayload.newStatus}</strong>?
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
                className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStatusConfirmYes}
                disabled={statusChangeLoading}
                className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {statusChangeLoading ? 'Updating...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {loading ? (
        <SkeletonTable cols={putawayColumns.length + 1} rows={8} />
      ) : (
        <Table
          columns={putawayColumns}
          data={putawayTasks}
          loading={false}
          emptyMessage="No putaway tasks found"
          actions={putawayRowActions}
          onRowClick={handlePutawayRowClick}
          selectable
          selectedRows={selectedRowIndices}
          onSelectRow={handleBulkSelectRow}
          onSelectAll={handleBulkSelectAll}
          selectAllChecked={selectAllChecked}
          selectAllIndeterminate={selectAllIndeterminate}
          selectAllLoading={selectAllLoading}
          defaultRowsPerPage={Math.max(putawayTasks.length, 1)}
        />
      )}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-[#667085]">
          {total > 0
            ? `Showing ${rangeStart}–${rangeEnd} of ${total} putaway${total === 1 ? '' : 's'}${filterStatus ? ` (${filterStatus})` : ''}`
            : 'No putaway tasks match the current filter'}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="h-9 rounded-lg border border-[#D0D5DD] bg-white px-3 text-sm text-[#344054] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="text-sm text-[#667085]">{`Page ${page} of ${totalPages}`}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="h-9 rounded-lg border border-[#D0D5DD] bg-white px-3 text-sm text-[#344054] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
